/**
 * TaskRow — the workhorse list-row primitive.
 *
 * Structure: a <div> container (NOT a button — chips need to be independently
 * clickable) with three interactive regions: the completion checkbox, a large
 * "open task" button wrapping title+meta, and a right cluster of chips + a
 * preview icon. Any chip becomes a Radix Popover trigger when its matching
 * `render*Editor` prop is supplied; the popover *content* (pickers + data +
 * mutations) is injected by the host so this package stays data-free.
 *
 * Compact variant (project chip hidden, person → bare avatar) is driven by the
 * PreviewLayout context so no screen has to thread a prop.
 */
import { useState, type ReactNode } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { Avatar } from './Avatar';
import { CtxBadge } from './CtxBadge';
import { DateChip, type DateChipState } from './DateChip';
import { Icon } from './Icon';
import { PersonChip } from './PersonChip';
import { PriorityDot, type TaskPriority } from './PriorityDot';
import { StatusChip, type TaskStatus } from './StatusChip';
import { usePreviewLayout } from '../layouts/PreviewLayoutContext';
import styles from './TaskRow.module.css';

export interface TaskRowModel {
  id: string;
  title: string;
  status: TaskStatus;
  priority: TaskPriority;
  done?: boolean;
  context?: { label: string; color: string } | null;
  projectName?: string | null;
  person?: { name: string; initials: string; color?: string | null } | null;
  dateLabel?: string | null;
  dateState?: DateChipState;
  /** Optional waiting-since label like "3d" — rendered in the meta row. */
  waitingSince?: string | null;
  /** Optional free-form note (e.g. "updated 3d ago") — rendered in the meta row, already worded. */
  metaNote?: string | null;
}

export interface TaskRowProps {
  task: TaskRowModel;
  onOpen?: (task: TaskRowModel) => void;
  onComplete?: (task: TaskRowModel) => void;
  /** Navigate to the full task page. Renders the trailing bare "open full" icon. */
  onOpenFull?: (task: TaskRowModel) => void;
  /** True for the row whose task is currently shown in the preview drawer. */
  previewing?: boolean;
  showProject?: boolean;
  showStatus?: boolean;
  renderStatusEditor?: (close: () => void) => ReactNode;
  renderPriorityEditor?: (close: () => void) => ReactNode;
  renderDueEditor?: (close: () => void) => ReactNode;
  renderPersonEditor?: (close: () => void) => ReactNode;
}

/**
 * Wraps a chip in a Radix Popover when `render` is provided, else renders it
 * plain. The editor render-prop receives a `close` callback it calls after a
 * pick; Radix also closes on outside-click / Esc.
 */
function EditableChip({
  render,
  children,
  ariaLabel,
}: {
  render?: ((close: () => void) => ReactNode) | undefined;
  children: ReactNode;
  ariaLabel: string;
}) {
  const [open, setOpen] = useState(false);
  if (!render) return <span className={styles.chipStatic}>{children}</span>;
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          className={styles.chipBtn}
          aria-label={ariaLabel}
          onClick={(e) => e.stopPropagation()}
        >
          {children}
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content side="bottom" align="end" sideOffset={6} className={styles.popover}>
          {render(() => setOpen(false))}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

export function TaskRow({
  task,
  onOpen,
  onComplete,
  onOpenFull,
  previewing = false,
  showProject = true,
  showStatus = false,
  renderStatusEditor,
  renderPriorityEditor,
  renderDueEditor,
  renderPersonEditor,
}: TaskRowProps) {
  const { open: previewOpen } = usePreviewLayout();
  const compact = previewOpen;

  const checkboxClass = [
    styles.checkbox,
    task.priority === 'critical' && styles.checkboxCritical,
    task.priority === 'important' && styles.checkboxImportant,
    task.done && styles.checkboxChecked,
  ]
    .filter(Boolean)
    .join(' ');

  // Overdue rows get the --neg left stripe (Aurora §4). Derived from the row's
  // existing date state; completed rows never show it.
  const overdue = !task.done && task.dateState === 'overdue';

  const rowClass = [
    styles.row,
    task.done && styles.done,
    previewing && styles.previewing,
    overdue && styles.overdue,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={rowClass} data-compact={compact ? '' : undefined}>
      <button
        type="button"
        aria-label={task.done ? 'Mark task incomplete' : 'Mark task complete'}
        className={checkboxClass}
        onClick={() => onComplete?.(task)}
      />
      <button type="button" className={styles.openTarget} onClick={() => onOpen?.(task)}>
        <div className={styles.body}>
          <div className={styles.title}>{task.title}</div>
          <div className={styles.meta}>
            {showStatus && (
              <>
                <StatusChip status={task.status} />
                <span className={styles.sep}>·</span>
              </>
            )}
            {task.context && <CtxBadge label={task.context.label} color={task.context.color} />}
            {showProject && task.projectName && (
              <span className={styles.projChipWrap}>
                <span className={styles.sep}>·</span>
                <span className={styles.projChip}>{task.projectName}</span>
              </span>
            )}
            {task.waitingSince && (
              <>
                <span className={styles.sep}>·</span>
                <span>waiting {task.waitingSince}</span>
              </>
            )}
            {task.metaNote && (
              <>
                <span className={styles.sep}>·</span>
                <span>{task.metaNote}</span>
              </>
            )}
          </div>
        </div>
      </button>
      <div className={styles.right}>
        {showStatus && (
          <EditableChip render={renderStatusEditor} ariaLabel="Edit status">
            <StatusChip status={task.status} />
          </EditableChip>
        )}
        {task.person && (
          <EditableChip render={renderPersonEditor} ariaLabel="Edit assignee">
            {compact ? <Avatar person={task.person} size={16} /> : <PersonChip person={task.person} />}
          </EditableChip>
        )}
        {task.dateLabel && (
          <EditableChip render={renderDueEditor} ariaLabel="Edit due date">
            <DateChip label={task.dateLabel} state={task.dateState ?? 'normal'} />
          </EditableChip>
        )}
        <EditableChip render={renderPriorityEditor} ariaLabel="Edit priority">
          <PriorityDot priority={task.priority} />
        </EditableChip>
        {onOpenFull && (
          <button
            type="button"
            className={styles.openFull}
            aria-label="Open full task"
            onClick={(e) => {
              e.stopPropagation();
              onOpenFull(task);
            }}
          >
            <Icon name="openFull" size={20} />
          </button>
        )}
      </div>
    </div>
  );
}
